<template>
  <metainfo></metainfo>
  <template v-if="disabledFence">
    <template v-if="isPlayer && !mobile">
      <RouterView />
    </template>
    <div v-else v-bind:class="{ mobile: mobile }">
      <div class="reloading" v-if="reloading">
        <LoadingSpinner />
      </div>
      <transparent-header v-if="displayTransparentHeader"></transparent-header>
      <top-menu
        v-else-if="
          isEntitled || (trialEntitlement && isLoggedIn && !displayLogoutOnly)
        "
      ></top-menu>
      <landing-header v-else-if="!hideHeader"></landing-header>
      <div class="page-content" :class="{ loading: contentLoading }">
        <RouterView />
      </div>
      <footer-component v-if="!hideFooter" />
      <CreateAccount
        :model-value="getShowCreateAccountDialog"
        @update:model-value="setShowCreateAccountDialog($event)"
        @close="setShowCreateAccountDialog(false)"
        @success="onCreateAccountSuccess"
        @login="onCreateAccountLogin"
      />
      <MagicLinkSent
        :model-value="getShowMagicLinkSentDialog"
        :email="getMagicLinkEmail"
        @update:model-value="setShowMagicLinkSentDialog($event)"
        @close="setShowMagicLinkSentDialog(false)"
      />
      <CreatePassword
        :model-value="getShowCreatePasswordDialog"
        :email="getCreatePasswordEmail"
        @update:model-value="setShowCreatePasswordDialog($event)"
        @close="setShowCreatePasswordDialog(false)"
      />
      <UnlockBenefits
        :model-value="getShowUnlockBenefitsDialog"
        @update:model-value="setShowUnlockBenefitsDialog($event)"
        @close="setShowUnlockBenefitsDialog(false)"
        @login="onUnlockBenefitsLogin"
        @start-now="onUnlockBenefitsStartNow"
      />
      <UnlockMoreBenefits
        :model-value="getShowUnlockMoreBenefitsDialog"
        @update:model-value="setShowUnlockMoreBenefitsDialog($event)"
        @close="setShowUnlockMoreBenefitsDialog(false)"
        @dismiss="setShowUnlockMoreBenefitsDialog(false)"
        @upgrade="onUnlockMoreBenefitsUpgrade"
      />
    </div>
  </template>
  <notus v-else />
  <div
    v-if="this.$route.name === 'lp-content' && mobile"
    class="signup-mobile text-center align-items-center"
    :class="[isScrolled ? 'd-flex' : 'd-none']"
  >
    <a
      class="text-white"
      :aria-label="$t('navbar.subscribe')"
      :href="`https://flixlatino.com/register`"
    >
      {{ $t("discover.start_now") }}
    </a>
  </div>
</template>
<style lang="scss" scoped>
@import "@/assets/scss/colors";
.signup-mobile {
  display: none;
  @media only screen and (min-width: 320px) and (max-width: 767px) {
    display: block;
    width: 100%;
    position: fixed;
    height: 4rem;
    bottom: 0;
    left: 0;
    margin: 0 0 0 0;
    z-index: 1;
    background-color: $red;
  }
  & a {
    font-size: x-large;
    width: 100%;
    display: block;
  }
}
@keyframes pulse {
  0% {
    opacity: 1;
  }
  10% {
    opacity: 0.5;
  }
  70% {
    opacity: 1;
  }
  100% {
    opacity: 0.5;
  }
}
.reloading {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #0c111b;
  z-index: 999;
  animation: pulse 8s infinite linear alternate;
}
</style>
<script>
import packageJson from "../package.json";
import { RouterView } from "vue-router";
import LandingHeader from "./components/generic/landingHeader.vue";
import TransparentHeader from "./components/generic/TransparentHeader.vue";
import FooterComponent from "./components/generic/FooterComponent.vue";
import { mapActions, mapState } from "pinia";
import { UserStore } from "./stores/userStore";
import { AssetStore } from "./stores/assetStore";
import { ProfileStore } from "./stores/profileStore";
import { UIStore } from "./stores/uiStore";
import TopMenu from "./components/app/partials/TopMenu.vue";
import LoadingSpinner from "./components/generic/LoadingSpinner.vue";
import CreatePassword from "./components/avod/dialogs/CreatePassword.vue";
import CreateAccount from "./components/avod/dialogs/CreateAccount.vue";
import MagicLinkSent from "./components/avod/dialogs/MagicLinkSent.vue";
import UnlockBenefits from "./components/avod/dialogs/UnlockBenefits.vue";
import UnlockMoreBenefits from "./components/avod/dialogs/UnlockMoreBenefits.vue";
import Bugsnag from "@bugsnag/js";
import Notus from "@/views/sales/notus.vue";
import axios from "axios";
import { FlixlatinoApiClient } from "@/services/FlixlatinoApiClient";

import { OrizonApiClient } from "@/services/OrizonApiClient";
import Cookie from "@/services/Cookie";
import { SessionStore } from "@/stores/sessionStore";
import KnowledgeTrans from "@/knowledge";
import MetaService from "./services/MetaService";

const orizonApiClient = new OrizonApiClient();

let currentRoute = undefined;

export default {
  name: "App",
  components: {
    Notus,
    TopMenu,
    LandingHeader,
    TransparentHeader,
    FooterComponent,
    RouterView,
    LoadingSpinner,
    CreatePassword,
    CreateAccount,
    MagicLinkSent,
    UnlockBenefits,
    UnlockMoreBenefits,
  },
  beforeRouteEnter(to, from, next) {
    console.log("ROUTE", to);
    currentRoute = to.name;
    next();
  },
  computed: {
    ...mapState(UserStore, [
      "getLanguage",
      "isEntitled",
      "userAccount",
      "isLoggedIn",
      "getToken",
      "trialEntitlement",
    ]),
    ...mapState(ProfileStore, ["getCurrentProfile"]),
    ...mapState(UIStore, [
      "getShowCreatePasswordDialog",
      "getCreatePasswordEmail",
      "getShowCreateAccountDialog",
      "getShowMagicLinkSentDialog",
      "getMagicLinkEmail",
      "getShowUnlockBenefitsDialog",
      "getShowUnlockMoreBenefitsDialog",
    ]),
    ...mapState(SessionStore, ["getFreemiumWorkflow"]),
    disabledFence() {
      let routeList = ["terms-and-conditions", "privacy-policy"];
      if (routeList.includes(this.$route.name)) {
        return true;
      }
      return import.meta.env.DEV ? true : this.fenceAccess;
    },
    mobile() {
      return this.isMobile();
    },
    isPlayer() {
      return this.$route.matched[1]?.meta?.isPlayer;
    },
    version: () => packageJson.version,
    hideFooter() {
      let hideFooter = [
        "profiles",
        "profile-add",
        "search",
        "register",
        "checkout",
        "plans-logged",
      ];

      return hideFooter.includes(this.$route.name);
    },
    hideHeader() {
      let hideHeader = [""];

      return hideHeader.includes(this.$route.name);
    },
    displayLogoutOnly() {
      return (
        this.$route.name === "register" ||
        this.$route.name === "checkout" ||
        this.$route.name === "plans-logged" ||
        this.$route.name === "profiles"
      );
    },
    displayTransparentHeader() {
      let routes = ["available-on", "lp-content", "home2"];

      return routes.includes(this.$route.name);
    },
  },
  data() {
    return {
      fenceAccess: true,
      reloading: false,
      reloadingStartedAt: 0,
      hitFence: null,
      contentLoading: false,
      resizeDebounce: null,
      isScrolled: false,
      watchCrossTabAuth: false,
      crossTabAuthSnapshot: "",
      authChangeSignalSeen: "",
      wasLoggedIn: false,
      lastProfileRefreshKey: "",
      assetStore: null,
    };
  },
  created() {
    this.assetStore = AssetStore();
    if (this.getLanguage !== this.$i18n.locale) {
      this.$i18n.locale = this.getLanguage;
      this.setLanguage(this.getLanguage);
    }
    this.reportInit();
    this.hitFence = setInterval(async () => {
      if (window.localStorage.getItem("data_enrichment") !== null) {
        this.checkFence();
        clearInterval(this.hitFence);
      }
    }, 2000);
    console.info("Current Version", this.version);
    console.info("Current Stored Lang", this.getLanguage);
    console.info("Current Locale Lang", this.$i18n.locale);

    setTimeout(() => {
      this.$i18n.locale = axios.defaults.params["lang"];
    }, 100);
    this.captureAuthChangeSignal();
    this.wasLoggedIn = this.isLoggedIn;
    this.lastProfileRefreshKey = this.getCurrentProfileKey(this.getCurrentProfile);
  },
  mounted() {
    window.addEventListener("scroll", this.handleScroll);
    window.addEventListener("blur", this.handleWindowBlur);
    window.addEventListener("focus", this.handleWindowFocus);
    this.setKnowledgeContent();
    this.$bus.on("switchProfile", (profile) => {
      this.setProfile(profile);
    });
    this.$bus.on("langChanged", () => {
      if (this.isLoggedIn) {
        this.loggedInReload();
      }
      this.setKnowledgeContent();
    });
    this.$bus.on("contentLoading", (status) => {
      this.contentLoading = status;
    });
    this.$bus.on("reloading", (status) => {
      const nextStatus = !!status;
      if (nextStatus) {
        this.reloadingStartedAt = Date.now();
      }
      this.reloading = nextStatus;
    });

    this.updateSize();
    window.addEventListener("resize", this.updateSizeDebounce);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.updateSizeDebounce);
    window.removeEventListener("scroll", this.handleScroll);
    window.removeEventListener("blur", this.handleWindowBlur);
    window.removeEventListener("focus", this.handleWindowFocus);
  },
  watch: {
    isLoggedIn(value, previousValue) {
      const wasLoggedOut = previousValue === false || this.wasLoggedIn === false;
      const wasLoggedIn = previousValue === true || this.wasLoggedIn === true;

      if (value && wasLoggedOut) {
        this.handleSuccessfulLogin();
      }

      if (!value && wasLoggedIn) {
        this.handleSuccessfulLogout();
      }

      this.wasLoggedIn = value;
      if (!value) {
        this.lastProfileRefreshKey = "";
      }
    },
    getCurrentProfile(profile) {
      const nextProfileKey = this.getCurrentProfileKey(profile);
      const previousProfileKey = this.lastProfileRefreshKey;
      this.lastProfileRefreshKey = nextProfileKey;

      if (!this.isLoggedIn || !nextProfileKey || nextProfileKey === previousProfileKey) {
        return;
      }

      this.handleSuccessfulProfileSwitch();
    },
    $route() {
      if (typeof window !== "undefined") {
        const bypassPlansAfterMagicLink =
          window.localStorage.getItem("flx:magic-link:bypass-plans") === "1";
        if (
          bypassPlansAfterMagicLink &&
          !["profiles", "link", "linkCode", "login", "register"].includes(
            String(this.$route?.name || "")
          )
        ) {
          window.localStorage.removeItem("flx:magic-link:bypass-plans");
        }
      }

      if (!this.reloading) {
        return;
      }
      const elapsed = Date.now() - Number(this.reloadingStartedAt || 0);
      const minimumVisibleMs = 350;
      const remaining = Math.max(0, minimumVisibleMs - elapsed);

      this.$nextTick(() => {
        window.setTimeout(() => {
          requestAnimationFrame(() => {
            this.reloading = false;
          });
        }, remaining);
      });
    },
  },
  methods: {
    ...mapActions(UserStore, ["setLanguage", "setFlMark", "setEntrypoint"]),
    ...mapActions(UIStore, [
      "setUiSize",
      "setShowCreatePasswordDialog",
      "setShowCreateAccountDialog",
      "setShowMagicLinkSentDialog",
      "setMagicLinkEmail",
      "setShowUnlockBenefitsDialog",
      "setShowUnlockMoreBenefitsDialog",
    ]),
    onCreateAccountSuccess(payload) {
      this.setMagicLinkEmail(String(payload?.email || ""));
      if (this.$route?.fullPath) {
        this.setEntrypoint(this.$route.fullPath);
      }
      if (typeof window !== "undefined") {
        window.localStorage.setItem("flx:magic-link:bypass-plans", "1");
      }
      this.setShowCreateAccountDialog(false);
      this.setShowMagicLinkSentDialog(true);
      this.watchCrossTabAuth = true;
      this.captureCrossTabAuthSnapshot();
      this.captureAuthChangeSignal();
    },
    onCreateAccountLogin() {
      this.setShowCreateAccountDialog(false);
      this.$router.push({ name: "login" });
    },
    onUnlockBenefitsLogin() {
      this.setShowUnlockBenefitsDialog(false);
      this.$router.push({ name: "login" });
    },
    onUnlockBenefitsStartNow() {
      this.setShowUnlockBenefitsDialog(false);
      this.$router.push({
        name: this.isLoggedIn ? "plans-logged" : "plans",
      });
    },
    onUnlockMoreBenefitsUpgrade() {
      this.setShowUnlockMoreBenefitsDialog(false);
      this.$router.push({
        name: this.isLoggedIn ? "plans-logged" : "plans",
      });
    },
    captureCrossTabAuthSnapshot() {
      if (typeof window === "undefined") {
        return;
      }
      this.crossTabAuthSnapshot = window.localStorage.getItem("flxUser") || "";
    },
    captureAuthChangeSignal() {
      if (typeof window === "undefined") {
        return;
      }
      this.authChangeSignalSeen =
        window.localStorage.getItem("flx:auth-changed-at") || "";
    },
    handleWindowBlur() {
      if (!this.watchCrossTabAuth) {
        return;
      }
      this.captureCrossTabAuthSnapshot();
    },
    handleWindowFocus() {
      if (typeof window === "undefined") {
        return;
      }

      const latestAuthSignal =
        window.localStorage.getItem("flx:auth-changed-at") || "";
      if (latestAuthSignal && latestAuthSignal !== this.authChangeSignalSeen) {
        this.authChangeSignalSeen = latestAuthSignal;
        window.location.reload();
        return;
      }

      if (!this.watchCrossTabAuth) {
        return;
      }
      const latestSnapshot = window.localStorage.getItem("flxUser") || "";
      if (latestSnapshot !== this.crossTabAuthSnapshot) {
        window.location.reload();
      }
    },
    handleScroll() {
      this.isScrolled =
        window.scrollY > 200 && this.$route.name === "lp-content";
    },
    markHomeForAuthRefresh() {
      if (typeof window === "undefined") {
        return;
      }
      window.sessionStorage.setItem("flx:home:force-reload", "1");
      window.sessionStorage.removeItem("flx:home:loaded");
    },
    getCurrentProfileKey(profile) {
      if (!profile) {
        return "";
      }

      return String(
        profile.id || profile.profile_id || profile.uuid || profile.name || ""
      );
    },
    refreshHomeIfVisible() {
      if (["home", "home-app"].includes(String(this.$route?.name || ""))) {
        window.location.reload();
      }
    },
    handleSuccessfulLogin() {
      this.assetStore?.setContinueWatching(false);
      this.markHomeForAuthRefresh();
      this.refreshHomeIfVisible();
    },
    handleSuccessfulProfileSwitch() {
      this.markHomeForAuthRefresh();
      this.refreshHomeIfVisible();
    },
    handleSuccessfulLogout() {
      this.markHomeForAuthRefresh();
    },
    loggedInReload() {
      this.reloading = true;
      let profile = this.getCurrentProfile;
      let apiClient = new FlixlatinoApiClient();
      apiClient.getInitData(profile).then(() => {
        // this.$bus.emit("reloading", false);
        this.reloading = false;
      });
    },
    getAllParameters() {
      const urlParams = new URLSearchParams(window.location.search);
      const parameters = {};

      const paramNames = [
        "traffic_type",
        "ga_client_id",
        "ga_session_id",
        "user_agent",
        "user_ip_address",
        "bot_score",
        "consent_status",
        "internal_campaign",
        "experiment_campaign_data",
        "microsoft_clarity_session_playback_url",
        "channel_grouping",
        "utm_id",
        "utm_source",
        "utm_medium",
        "utm_campaign",
        "utm_source_platform",
        "utm_content",
        "utm_creative_format",
        "utm_marketing_tactic",
        "utm_term",
        "gclid",
        "entrance_page",
        "channel_grouping_first_touch",
        "utm_id_first_touch",
        "utm_source_first_touch",
        "utm_medium_first_touch",
        "utm_campaign_first_touch",
        "utm_source_platform_first_touch",
        "utm_content_first_touch",
        "utm_creative_format_first_touch",
        "utm_marketing_tactic_first_touch",
        "utm_term_first_touch",
        "gclid_first_touch",
        "timestamp_first_touch",
        "entrance_page_first_touch",
        "microsoft_click_id",
        "twitter_click_id",
        "facebook_browser_id",
        "facebook_click_id",
        "tiktok_browser_id",
        "tiktok_click_id",
        "ref",
      ];

      paramNames.forEach((paramName) => {
        if (urlParams.has(paramName)) {
          parameters[paramName] = urlParams.get(paramName);
        }
      });

      return parameters;
    },
    objectToQueryString(obj) {
      return Object.entries(obj)
        .map(
          ([key, value]) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
        )
        .join("&");
    },
    checkFence() {
      let allUtms = this.getAllParameters();
      let queryString = `${this.objectToQueryString(allUtms)}`;
      queryString = queryString !== "" ? `${queryString}&` : "";
      let queryString2 =
        window.localStorage.getItem("data_enrichment") !== null
          ? `${this.objectToQueryString(
              JSON.parse(window.localStorage.getItem("data_enrichment"))
            )}`
          : "";

      let clarity =
        window.clarity_user_id !== undefined
          ? `clarity_user_id=${window.clarity_user_id}&`
          : "";

      if (allUtms["ref"] !== undefined) {
        Cookie.createTapAffiliateCookie(allUtms["ref"]);
        window.localStorage.setItem("ta_ref", allUtms["ref"]);
      }

      orizonApiClient
        .get(`/api/?l=fence&${clarity}${queryString}${queryString2}`, true)
        .then((response) => {
          if (!response.data.error) {
            this.fenceAccess = response.data.access;
            this.markTheUser(response.data.fl_mark);
          }
        });
    },
    markTheUser(cookieValue) {
      this.setFlMark(cookieValue);
      var name = "fl_mark";
      var value = cookieValue;
      var expirationDays = 90;
      var domain = ".flixlatino.com";
      var path = "/";

      var expirationDate = new Date();
      expirationDate.setTime(
        expirationDate.getTime() + expirationDays * 24 * 60 * 60 * 1000
      );
      var expires = "expires=" + expirationDate.toUTCString();

      document.cookie =
        name +
        "=" +
        value +
        ";" +
        expires +
        ";path=" +
        path +
        ";domain=" +
        domain +
        ";Secure";
    },
    setProfile(profile) {
      this.$router.push({ name: "profiles" }).then(() => {
        this.$bus.emit("setProfile", profile);
      });
    },
    reportInit() {
      if (this.isLoggedIn) {
        window.bn = Bugsnag;
        // @ts-ignore
        const userId = this.userAccount.user_id;
        // @ts-ignore
        const username = this.userAccount.username;
        // @ts-ignore
        const name = this.userAccount.name;
        Bugsnag.setUser(userId, username, name);
      }
    },
    updateSize() {
      this.setUiSize(window.innerWidth, window.innerHeight, this.isMobile());
    },
    updateSizeDebounce() {
      if (this.resizeDebounce) {
        clearTimeout(this.resizeDebounce);
      }
      this.resizeDebounce = setTimeout(() => {
        this.updateSize();
      }, 100);
    },
    setKnowledgeContent() {
      let knowledge = KnowledgeTrans[this.$i18n.locale];
      if (!knowledge) {
        return;
      }
      MetaService.setKnowledgeContent(JSON.stringify(knowledge));
    },
  },
};
</script>
